import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosRequestConfig } from 'axios';
import api from '../../api/api';

interface RoomType {
  id: number;
  name: string;
  description: string;
  maxGuests: number;
  baseNightlyRate: number;
  isActive: boolean;
  version: number;
}

interface Room {
  id: number;
  roomNumber: string;
  roomTypeId: number;
  roomTypeName: string;
  floor: number | null;
  housekeepingStatus: string;
  isActive: boolean;
  version: number;
}

interface Tariff {
  id: number;
  roomTypeId: number;
  roomTypeName: string;
  name: string;
  validFrom: string;
  validTo: string;
  nightlyRate: number;
  isActive: boolean;
  version: number;
}

interface Guest {
  id: number;
  name: string;
  contacts: string;
}

interface Reservation {
  id: number;
  crmCustomerId: number;
  customerName: string;
  roomId: number;
  roomNumber: string;
  checkInDate: string;
  checkOutDate: string;
  guestCount: number;
  status: string;
  totalRoomCharge: number;
  chargesTotal: number;
  paymentsTotal: number;
  balance: number;
  notes: string;
  version: number;
  cancellationReason?: string | null;
  actualCheckInDate: string | null;
  actualCheckOutDate: string | null;
}

interface FolioCharge {
  id: number;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  createdAtUtc: string;
}

interface FolioPayment {
  id: number;
  paidAtUtc: string;
  amount: number;
  method: string;
  reference: string;
}

interface Folio {
  reservationId: number;
  charges: FolioCharge[];
  payments: FolioPayment[];
  chargesTotal: number;
  paymentsTotal: number;
  balance: number;
}

type Tab = 'reservations' | 'rooms' | 'tariffs';
type RoomTypeDraft = { name: string; description: string; maxGuests: string; baseNightlyRate: string };
type RoomDraft = { roomNumber: string; roomTypeId: string; floor: string };
type TariffDraft = { roomTypeId: string; name: string; validFrom: string; validTo: string; nightlyRate: string };
type ReservationDraft = { crmCustomerId: string; roomId: string; checkInDate: string; checkOutDate: string; guestCount: string; notes: string };

const emptyRoomType: RoomTypeDraft = { name: '', description: '', maxGuests: '2', baseNightlyRate: '0' };
const emptyRoom: RoomDraft = { roomNumber: '', roomTypeId: '', floor: '' };
const emptyTariff: TariffDraft = { roomTypeId: '', name: '', validFrom: '', validTo: '', nightlyRate: '' };
const emptyReservation: ReservationDraft = { crmCustomerId: '', roomId: '', checkInDate: '', checkOutDate: '', guestCount: '1', notes: '' };
const panel: React.CSSProperties = { padding: 16, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-lg)', background: 'var(--crm-surface)', color: 'var(--crm-text)' };
const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: 8, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };
const button: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', cursor: 'pointer', whiteSpace: 'nowrap' };
const successButton: React.CSSProperties = { ...button, borderColor: 'var(--crm-success)', background: 'var(--crm-success-bg)', color: 'var(--crm-success)' };
const dangerButton: React.CSSProperties = { ...button, borderColor: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)' };
const neutralButton: React.CSSProperties = { ...button, borderColor: 'var(--crm-border)', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)' };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', minWidth: 720 };
const money = (value: number) => `${value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
const dateLabel = (value: string) => new Date(`${value}T00:00:00`).toLocaleDateString('uk-UA');
const errorMessage = (error: unknown) => {
  const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return responseMessage || 'Не вдалося виконати операцію.';
};

const HotelPage: React.FC = () => {
  const operations = useRef(new Map<string, string>());
  const loadVersion = useRef(0);
  const folioRequest = useRef(0);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [draft, setDraft] = useState<ReservationDraft>(emptyReservation);
  const [roomTypeDraft, setRoomTypeDraft] = useState<RoomTypeDraft>(emptyRoomType);
  const [roomDraft, setRoomDraft] = useState<RoomDraft>(emptyRoom);
  const [tariffDraft, setTariffDraft] = useState<TariffDraft>(emptyTariff);
  const [tab, setTab] = useState<Tab>('reservations');
  const [folio, setFolio] = useState<Folio | null>(null);
  const [folioReservation, setFolioReservation] = useState<Reservation | null>(null);
  const [chargeDraft, setChargeDraft] = useState({ description: '', quantity: '1', unitPrice: '' });
  const [paymentDraft, setPaymentDraft] = useState({ amount: '', method: 'Готівка', reference: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [canEdit, setCanEdit] = useState(false);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setError('');
    try {
      const [typeResponse, roomResponse, tariffResponse, guestResponse, reservationResponse, accessResponse] = await Promise.all([
        api.get<RoomType[]>('/hotel/room-types'),
        api.get<Room[]>('/hotel/rooms'),
        api.get<Tariff[]>('/hotel/tariffs'),
        api.get<Guest[]>('/hotel/guests'),
        api.get<Reservation[]>('/hotel/reservations'),
        api.get<{ editPermissions: string[] }>('/users/me/access'),
      ]);
      if (version !== loadVersion.current) return;
      setRoomTypes(typeResponse.data);
      setRooms(roomResponse.data);
      setTariffs(tariffResponse.data);
      setGuests(guestResponse.data);
      setReservations(reservationResponse.data);
      setCanEdit(accessResponse.data.editPermissions.includes('page.management.hotel'));
    } catch (loadError) {
      if (version !== loadVersion.current) return;
      setRoomTypes([]);
      setRooms([]);
      setTariffs([]);
      setGuests([]);
      setReservations([]);
      setCanEdit(false);
      ++folioRequest.current;
      setFolio(null);
      setFolioReservation(null);
      setError(errorMessage(loadError));
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const collectionCounter = loadVersion;
    const folioCounter = folioRequest;
    void load();
    return () => { ++collectionCounter.current; ++folioCounter.current; };
  }, [load]);

  useEffect(() => {
    setFolioReservation(current => current ? reservations.find(item => item.id === current.id) ?? null : null);
  }, [reservations]);

  const hotelPost = async (path: string, body: object, config?: AxiosRequestConfig) => {
    const identity = JSON.stringify([path, body, config?.headers?.['If-Match']]);
    let key = operations.current.get(identity);
    if (!key) {
      key = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
      operations.current.set(identity, key);
    }
    const response = await api.post(path, body, { ...config, headers: { ...config?.headers, 'Idempotency-Key': key } });
    operations.current.delete(identity);
    return response;
  };

  const mutate = async (operation: () => Promise<unknown>) => {
    if (!canEdit || saving) return false;
    let succeeded = false;
    setSaving(true);
    setError('');
    try {
      await operation();
      succeeded = true;
      await load();
      if (folioReservation) {
        const version = folioRequest.current;
        const response = await api.get<Folio>(`/hotel/reservations/${folioReservation.id}/folio`);
        if (version === folioRequest.current) setFolio(response.data);
      }
      return true;
    } catch (operationError) {
      const code = (operationError as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'idempotency-completed-refresh') {
        await load();
        if (folioReservation) {
          try {
            const version = folioRequest.current;
            const response = await api.get<Folio>(`/hotel/reservations/${folioReservation.id}/folio`);
            if (version === folioRequest.current) setFolio(response.data);
          } catch (refreshError) {
            setFolio(null);
            setFolioReservation(null);
            setError(errorMessage(refreshError));
            return succeeded;
          }
        }
      }
      if (succeeded) {
        setFolio(null);
        setFolioReservation(null);
      }
      setError(errorMessage(operationError));
      return succeeded;
    } finally {
      setSaving(false);
    }
  };

  const requestVersion = (reservation: Reservation) => ({ headers: { 'If-Match': `"${reservation.version}"` } });

  const submitReservation = async (event: React.FormEvent) => {
    event.preventDefault();
    const created = await mutate(() => hotelPost('/hotel/reservations', {
      crmCustomerId: Number(draft.crmCustomerId),
      roomId: Number(draft.roomId),
      checkInDate: draft.checkInDate,
      checkOutDate: draft.checkOutDate,
      guestCount: Number(draft.guestCount),
      notes: draft.notes,
    }));
    if (created) setDraft(emptyReservation);
  };

  const submitRoomType = async (event: React.FormEvent) => {
    event.preventDefault();
    const created = await mutate(() => hotelPost('/hotel/room-types', {
      name: roomTypeDraft.name,
      description: roomTypeDraft.description,
      maxGuests: Number(roomTypeDraft.maxGuests),
      baseNightlyRate: Number(roomTypeDraft.baseNightlyRate),
      isActive: true,
    }));
    if (created) setRoomTypeDraft(emptyRoomType);
  };

  const submitRoom = async (event: React.FormEvent) => {
    event.preventDefault();
    const created = await mutate(() => hotelPost('/hotel/rooms', {
      roomNumber: roomDraft.roomNumber,
      roomTypeId: Number(roomDraft.roomTypeId),
      floor: roomDraft.floor ? Number(roomDraft.floor) : null,
      housekeepingStatus: 'Ready',
      isActive: true,
    }));
    if (created) setRoomDraft(emptyRoom);
  };

  const submitTariff = async (event: React.FormEvent) => {
    event.preventDefault();
    const created = await mutate(() => hotelPost('/hotel/tariffs', {
      roomTypeId: Number(tariffDraft.roomTypeId),
      name: tariffDraft.name,
      validFrom: tariffDraft.validFrom,
      validTo: tariffDraft.validTo,
      nightlyRate: Number(tariffDraft.nightlyRate),
      isActive: true,
    }));
    if (created) setTariffDraft(emptyTariff);
  };

  const transition = async (reservation: Reservation, action: 'check-in' | 'check-out' | 'cancel') => {
    const confirmed = action === 'cancel' ? window.confirm('Скасувати це бронювання?') : true;
    if (!confirmed) return;
    const reason = action === 'cancel' ? window.prompt('Вкажіть причину скасування:') : '';
    if (action === 'cancel' && !reason?.trim()) return;
    await mutate(() => hotelPost(`/hotel/reservations/${reservation.id}/${action}`,
      action === 'check-in'
        ? { actualCheckInDate: null }
        : action === 'check-out'
          ? { actualCheckOutDate: null }
          : { reason },
      requestVersion(reservation)));
  };

  const setHousekeeping = async (room: Room, status: string) => {
    await mutate(() => api.put(`/hotel/rooms/${room.id}/housekeeping`, { status }, {
      headers: { 'If-Match': `"${room.version}"` },
    }));
  };

  const openFolio = async (reservation: Reservation) => {
    const version = ++folioRequest.current;
    setFolio(null);
    setFolioReservation(null);
    setError('');
    try {
      const response = await api.get<Folio>(`/hotel/reservations/${reservation.id}/folio`);
      if (version !== folioRequest.current) return;
      setFolio(response.data);
      setFolioReservation(reservation);
      setChargeDraft({ description: '', quantity: '1', unitPrice: '' });
      setPaymentDraft({ amount: '', method: 'Готівка', reference: '' });
    } catch (folioError) {
      if (version === folioRequest.current) setError(errorMessage(folioError));
    }
  };

  const closeFolio = () => {
    ++folioRequest.current;
    setFolio(null);
    setFolioReservation(null);
  };

  const addCharge = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!folioReservation) return;
    const saved = await mutate(() => hotelPost(`/hotel/reservations/${folioReservation.id}/charges`, {
      description: chargeDraft.description,
      quantity: Number(chargeDraft.quantity),
      unitPrice: Number(chargeDraft.unitPrice),
    }));
    if (saved) {
      setChargeDraft({ description: '', quantity: '1', unitPrice: '' });
    }
  };

  const addPayment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!folioReservation) return;
    const saved = await mutate(() => hotelPost(`/hotel/reservations/${folioReservation.id}/payments`, {
      amount: Number(paymentDraft.amount),
      method: paymentDraft.method,
      reference: paymentDraft.reference,
    }));
    if (saved) {
      setPaymentDraft({ amount: '', method: 'Готівка', reference: '' });
    }
  };

  const header = (title: string) => <h3 style={{ margin: '0 0 12px', color: 'var(--crm-text)' }}>{title}</h3>;
  const field = (label: string, value: string, onChange: (value: string) => void, type = 'text', required = true) => (
    <label style={{ display: 'grid', gap: 5, fontSize: 13, color: 'var(--crm-text)' }}>
      {label}
      <input required={required} type={type} value={value} onChange={event => onChange(event.target.value)} style={input} />
    </label>
  );
  const select = (label: string, value: string, onChange: (value: string) => void, options: Array<{ value: string; label: string }>) => (
    <label style={{ display: 'grid', gap: 5, fontSize: 13, color: 'var(--crm-text)' }}>
      {label}
      <select required value={value} onChange={event => onChange(event.target.value)} style={input}>
        <option value="">Оберіть…</option>
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );

  return (
    <main className="page-shell" style={{ padding: 24, maxWidth: 1500, margin: '0 auto', color: 'var(--crm-text)' }}>
      <h2 style={{ borderBottom: '2px solid var(--crm-accent)', paddingBottom: 8 }}>Готель: бронювання та проживання</h2>
      <p style={{ color: 'var(--crm-text-muted)' }}>Номери, тарифи, гості та фоліо належать лише обраному ресторану. Нарахування за фактичні ночі формуються під час виїзду.</p>
      {error && <div role="alert" style={{ padding: 12, marginBottom: 14, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)' }}>{error}</div>}
      <nav aria-label="Розділи готелю" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {([['reservations', 'Бронювання'], ['rooms', 'Номери'], ['tariffs', 'Тарифи']] as Array<[Tab, string]>).map(([value, label]) => (
          <button key={value} type="button" aria-pressed={tab === value} onClick={() => setTab(value)} style={tab === value ? { ...button, background: 'var(--crm-primary)', borderColor: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)' } : neutralButton}>{label}</button>
        ))}
        <button type="button" onClick={() => void load()} disabled={loading} style={{ ...neutralButton, marginLeft: 'auto' }}>Оновити</button>
      </nav>

      {tab === 'reservations' && <>
        {canEdit && <form onSubmit={submitReservation} style={{ ...panel, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
          {header('Нове бронювання')}
          {select('CRM-картка гостя', draft.crmCustomerId, value => setDraft(current => ({ ...current, crmCustomerId: value })),
            guests.map(guest => ({ value: String(guest.id), label: `${guest.name}${guest.contacts ? ` — ${guest.contacts}` : ''}` })))}
          {select('Номер', draft.roomId, value => setDraft(current => ({ ...current, roomId: value })),
            rooms.filter(room => room.isActive).map(room => ({ value: String(room.id), label: `${room.roomNumber} · ${room.roomTypeName}` })))}
          {field('Заїзд', draft.checkInDate, value => setDraft(current => ({ ...current, checkInDate: value })), 'date')}
          {field('Виїзд', draft.checkOutDate, value => setDraft(current => ({ ...current, checkOutDate: value })), 'date')}
          {field('Гостей', draft.guestCount, value => setDraft(current => ({ ...current, guestCount: value })), 'number')}
          {field('Примітка', draft.notes, value => setDraft(current => ({ ...current, notes: value })), 'text', false)}
          <button type="submit" disabled={saving || !rooms.length || !guests.length} style={{ ...button, alignSelf: 'end' }}>Створити бронювання</button>
        </form>}
        <section style={{ ...panel, overflowX: 'auto' }}>
          {header('Список бронювань')}
          <table style={table}>
            <thead><tr>{['Гість', 'Номер', 'Період', 'Гостей', 'Статус', 'Фоліо / баланс', 'Дії'].map(item => <th key={item} style={{ padding: 9, textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>{item}</th>)}</tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ padding: 18, textAlign: 'center' }}>Завантаження…</td></tr>}
              {!loading && reservations.map(reservation => (
                <tr key={reservation.id}>
                  <td style={{ padding: 9, borderBottom: '1px solid var(--crm-border)' }}>{reservation.customerName}</td>
                  <td style={{ padding: 9, borderBottom: '1px solid var(--crm-border)' }}>{reservation.roomNumber}</td>
                  <td style={{ padding: 9, borderBottom: '1px solid var(--crm-border)', whiteSpace: 'nowrap' }}>{dateLabel(reservation.checkInDate)} – {dateLabel(reservation.checkOutDate)}</td>
                  <td style={{ padding: 9, borderBottom: '1px solid var(--crm-border)' }}>{reservation.guestCount}</td>
                  <td style={{ padding: 9, borderBottom: '1px solid var(--crm-border)' }}>{reservation.status}</td>
                  <td style={{ padding: 9, borderBottom: '1px solid var(--crm-border)', whiteSpace: 'nowrap' }}>{money(reservation.balance)}</td>
                  <td style={{ padding: 9, borderBottom: '1px solid var(--crm-border)' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {canEdit && reservation.status === 'Confirmed' && <>
                        <button type="button" disabled={saving} onClick={() => void transition(reservation, 'check-in')} style={successButton}>Заселити</button>
                        <button type="button" disabled={saving} onClick={() => void transition(reservation, 'cancel')} style={dangerButton}>Скасувати</button>
                      </>}
                      {canEdit && reservation.status === 'CheckedIn' && <button type="button" disabled={saving} onClick={() => void transition(reservation, 'check-out')} style={successButton}>Виселити</button>}
                      {reservation.status !== 'Cancelled' && <button type="button" onClick={() => void openFolio(reservation)} style={neutralButton}>Фоліо</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && reservations.length === 0 && <tr><td colSpan={7} style={{ padding: 18, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Бронювань ще немає.</td></tr>}
            </tbody>
          </table>
        </section>
      </>}

      {tab === 'rooms' && <>
        {canEdit && <form onSubmit={submitRoomType} style={{ ...panel, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          {header('Новий тип номера')}
          {field('Назва', roomTypeDraft.name, value => setRoomTypeDraft(current => ({ ...current, name: value })))}
          {field('Опис', roomTypeDraft.description, value => setRoomTypeDraft(current => ({ ...current, description: value })), 'text', false)}
          {field('Максимум гостей', roomTypeDraft.maxGuests, value => setRoomTypeDraft(current => ({ ...current, maxGuests: value })), 'number')}
          {field('Базова ціна за ніч', roomTypeDraft.baseNightlyRate, value => setRoomTypeDraft(current => ({ ...current, baseNightlyRate: value })), 'number')}
          <button type="submit" disabled={saving} style={{ ...button, alignSelf: 'end' }}>Додати тип</button>
        </form>}
        {canEdit && <form onSubmit={submitRoom} style={{ ...panel, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          {header('Новий номер')}
          {field('Номер кімнати', roomDraft.roomNumber, value => setRoomDraft(current => ({ ...current, roomNumber: value })))}
          {select('Тип номера', roomDraft.roomTypeId, value => setRoomDraft(current => ({ ...current, roomTypeId: value })),
            roomTypes.filter(item => item.isActive).map(item => ({ value: String(item.id), label: item.name })))}
          {field('Поверх', roomDraft.floor, value => setRoomDraft(current => ({ ...current, floor: value })), 'number', false)}
          <button type="submit" disabled={saving || !roomTypes.length} style={{ ...button, alignSelf: 'end' }}>Додати номер</button>
        </form>}
        <section style={{ ...panel, overflowX: 'auto' }}>
          {header('Номери та типи')}
          <table style={table}>
            <thead><tr>{['Номер', 'Тип', 'Поверх', 'Прибирання', 'Активний'].map(item => <th key={item} style={{ padding: 9, textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>{item}</th>)}</tr></thead>
            <tbody>{rooms.map(room => <tr key={room.id}>
              <td style={{ padding: 9 }}>{room.roomNumber}</td><td style={{ padding: 9 }}>{room.roomTypeName}</td>
              <td style={{ padding: 9 }}>{room.floor ?? '—'}</td>
              <td style={{ padding: 9 }}><select aria-label={`Прибирання номера ${room.roomNumber}`} value={room.housekeepingStatus} disabled={saving || !canEdit} onChange={event => void setHousekeeping(room, event.target.value)} style={input}>
                <option value="Ready">Готовий</option><option value="NeedsCleaning">Потребує прибирання</option><option value="OutOfService">Не працює</option>
              </select></td>
              <td style={{ padding: 9 }}>{room.isActive ? 'Так' : 'Ні'}</td>
            </tr>)}</tbody>
          </table>
          {roomTypes.length === 0 && <p>Спочатку створіть тип номера.</p>}
        </section>
      </>}

      {tab === 'tariffs' && <>
        {canEdit && <form onSubmit={submitTariff} style={{ ...panel, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          {header('Новий тариф')}
          {select('Тип номера', tariffDraft.roomTypeId, value => setTariffDraft(current => ({ ...current, roomTypeId: value })),
            roomTypes.filter(item => item.isActive).map(item => ({ value: String(item.id), label: item.name })))}
          {field('Назва тарифу', tariffDraft.name, value => setTariffDraft(current => ({ ...current, name: value })))}
          {field('Діє з', tariffDraft.validFrom, value => setTariffDraft(current => ({ ...current, validFrom: value })), 'date')}
          {field('Діє до (включно)', tariffDraft.validTo, value => setTariffDraft(current => ({ ...current, validTo: value })), 'date')}
          {field('Ціна за ніч', tariffDraft.nightlyRate, value => setTariffDraft(current => ({ ...current, nightlyRate: value })), 'number')}
          <button type="submit" disabled={saving || !roomTypes.length} style={{ ...button, alignSelf: 'end' }}>Додати тариф</button>
        </form>}
        <section style={{ ...panel, overflowX: 'auto' }}>
          {header('Тарифні періоди')}
          <table style={table}>
            <thead><tr>{['Тип номера', 'Тариф', 'Період', 'Ціна за ніч', 'Активний'].map(item => <th key={item} style={{ padding: 9, textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>{item}</th>)}</tr></thead>
            <tbody>{tariffs.map(tariff => <tr key={tariff.id}>
              <td style={{ padding: 9 }}>{tariff.roomTypeName}</td><td style={{ padding: 9 }}>{tariff.name}</td>
              <td style={{ padding: 9 }}>{dateLabel(tariff.validFrom)} – {dateLabel(tariff.validTo)}</td>
              <td style={{ padding: 9 }}>{money(tariff.nightlyRate)}</td><td style={{ padding: 9 }}>{tariff.isActive ? 'Так' : 'Ні'}</td>
            </tr>)}</tbody>
          </table>
        </section>
      </>}

      {folio && folioReservation && <div role="dialog" aria-modal="true" onClick={closeFolio} style={{ position: 'fixed', inset: 0, zIndex: 1500, display: 'grid', placeItems: 'center', padding: 16, background: 'rgba(15, 23, 42, .55)' }}>
        <section onClick={event => event.stopPropagation()} style={{ ...panel, width: 'min(900px, 96vw)', maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--crm-shadow-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {header(`Фоліо · ${folioReservation.customerName} · №${folioReservation.roomNumber}`)}
            <button type="button" onClick={closeFolio} style={neutralButton}>Закрити</button>
          </div>
          <p>Нараховано: <b>{money(folio.chargesTotal)}</b> · Оплачено: <b>{money(folio.paymentsTotal)}</b> · Баланс: <b>{money(folio.balance)}</b></p>
          <div style={{ overflowX: 'auto' }}><table style={table}>
            <thead><tr>{['Тип', 'Опис', 'Дата', 'Сума'].map(item => <th key={item} style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>{item}</th>)}</tr></thead>
            <tbody>
              {folio.charges.map(item => <tr key={`charge-${item.id}`}><td style={{ padding: 8 }}>Нарахування</td><td style={{ padding: 8 }}>{item.description}</td><td style={{ padding: 8 }}>{new Date(item.createdAtUtc).toLocaleString('uk-UA')}</td><td style={{ padding: 8 }}>{money(item.amount)}</td></tr>)}
              {folio.payments.map(item => <tr key={`payment-${item.id}`}><td style={{ padding: 8 }}>Оплата · {item.method}</td><td style={{ padding: 8 }}>{item.reference || '—'}</td><td style={{ padding: 8 }}>{new Date(item.paidAtUtc).toLocaleString('uk-UA')}</td><td style={{ padding: 8 }}>−{money(item.amount)}</td></tr>)}
            </tbody>
          </table></div>
          {canEdit && folioReservation.status === 'CheckedIn' && <form onSubmit={addCharge} className="hotel-folio-form hotel-folio-form--charge" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginTop: 16 }}>
            <input required aria-label="Додаткове нарахування" placeholder="Додаткове нарахування" value={chargeDraft.description} onChange={event => setChargeDraft(current => ({ ...current, description: event.target.value }))} style={input} />
            <input required aria-label="Кількість" type="number" min="0.001" step="0.001" placeholder="Кількість" value={chargeDraft.quantity} onChange={event => setChargeDraft(current => ({ ...current, quantity: event.target.value }))} style={input} />
            <input required aria-label="Ціна" type="number" min="0.01" step="0.01" placeholder="Ціна" value={chargeDraft.unitPrice} onChange={event => setChargeDraft(current => ({ ...current, unitPrice: event.target.value }))} style={input} />
            <button type="submit" disabled={saving} style={button}>Нарахувати</button>
          </form>}
          {canEdit && folioReservation.status !== 'Confirmed' && folioReservation.status !== 'Cancelled' && folio.balance > 0 && <form onSubmit={addPayment} className="hotel-folio-form hotel-folio-form--payment" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: 8, marginTop: 10 }}>
            <input required aria-label="Сума оплати" type="number" min="0.01" max={folio.balance} step="0.01" placeholder="Сума" value={paymentDraft.amount} onChange={event => setPaymentDraft(current => ({ ...current, amount: event.target.value }))} style={input} />
            <input required aria-label="Спосіб оплати" value={paymentDraft.method} onChange={event => setPaymentDraft(current => ({ ...current, method: event.target.value }))} style={input} />
            <input aria-label="Референс платежу" placeholder="Референс" value={paymentDraft.reference} onChange={event => setPaymentDraft(current => ({ ...current, reference: event.target.value }))} style={input} />
            <button type="submit" disabled={saving} style={button}>Прийняти оплату</button>
          </form>}
        </section>
      </div>}
    </main>
  );
};

export default HotelPage;
